import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Fade,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import talentIq from "../../assets/images/Talent_IQ_IMG_Trans.png";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from "../../providers/SnackbarProvider";
import {
  Visibility,
  Email,
  Lock,
  VisibilityOff,
  ArrowBack,
} from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import api from "../../api/client";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";

// Background images matching the login screen
const backgroundImages = [
  "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80",
  "https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80",
  "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
];

// OTP Input Component
const OTPInput = ({ value, onChange, length = 6, disabled = false }) => {
  const inputRefs = useRef([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const handleChange = (index, e) => {
    const inputValue = e.target.value.replace(/[^0-9]/, "");

    if (inputValue) {
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }

    const newOtp = value.split("");
    newOtp[index] = inputValue;
    onChange(newOtp.join(""));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, length);

    if (pasteData) {
      onChange(pasteData);
      const focusIndex = Math.min(pasteData.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", my: 2 }}>
      {Array.from({ length }, (_, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={value[index] || ""}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontSize: "1.2rem",
              fontWeight: "bold",
            },
          }}
          sx={{
            width: 50,
            height: 50,
            transform: isLoaded ? "scale(1)" : "scale(0)",
            opacity: isLoaded ? 1 : 0,
            transition: `all 0.4s ease-out ${0.2 + index * 0.1}s`,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: value[index] ? "#62a7f8" : "white",
              "& fieldset": {
                borderColor: value[index] ? "#62a7f8" : "#ddd",
                borderWidth: value[index] ? "2px" : "1px",
              },
              "&:hover fieldset": {
                borderColor: "#62a7f8",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#62a7f8",
                borderWidth: "2px",
              },
            },
            "& .MuiInputBase-input": {
              color: value[index] ? "white" : "#2b4660",
              fontWeight: "bold",
            },
          }}
        />
      ))}
    </Box>
  );
};

const schema = Yup.object().shape({
  otp: Yup.string()
    .required("OTP is required")
    .length(6, "OTP must be exactly 6 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*(),.?])[A-Za-z\d!@#$%^&*(),.?]{8,}$/,
      "Password must contain at least one uppercase letter, one lowercase letter, and one special character (!@#$%^&*(),.?)"
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const email =
    location.state?.email || localStorage.getItem("resetEmail") || "";
  const otpExpiryTime =
    location.state?.otpExpiryTime ||
    localStorage.getItem("otpExpiryTime") ||
    "";
  const [otp, setOtp] = useState("");
  const [otpExpired, setOtpExpired] = useState(false);
  const [isOtpEntered, setIsOtpEntered] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Background image carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
        setFadeIn(true);
      }, 200);
    }, 8000);

    // Trigger initial animation
    setTimeout(() => setIsLoaded(true), 100);

    return () => clearInterval(interval);
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      otp: "",
      password: "",
      confirmPassword: "",
    },
  });

  const handleOtpChange = (newOtp) => {
    setOtp(newOtp);
    setValue("otp", newOtp);
    setIsOtpEntered(newOtp.length === 6);
  };

  useEffect(() => {
    if (otpExpiryTime) {
      const now = new Date();
      const expiry = new Date(otpExpiryTime);
      const diffInMs = expiry.getTime() - now.getTime();
      const diffInSeconds = Math.floor(diffInMs / 1000);

      if (diffInSeconds > 0) {
        setTimeLeft(diffInSeconds);

        const interval = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              setOtpExpired(true);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(interval);
      } else {
        setOtpExpired(true);
      }
    }
  }, [otpExpiryTime, navigate]);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timeInSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleBack = () => {
    navigate("/forgot-password");
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        email: email,
        otp: Number(data.otp),
        new_password: data.password,
        confirmPassword: data.confirmPassword,
      };
      console.log("payload", payload);

      const response = await api.post("VerifyOTPAndResetPassword", payload);

      if (response?.data?.status === "success") {
        showSnackbar("success", response?.data?.message);
        navigate("/");
      } else {
        showSnackbar(
          "error",
          response?.data?.message || "Something went wrong"
        );
      }
    } catch (error) {
      console.error("Error during password reset:", error);
      showSnackbar("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        background: "black",
      }}
    >
      {/* Background Image Carousel */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        <Fade in={fadeIn} timeout={500}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${backgroundImages[currentBgIndex]})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "absolute",
            }}
          />
        </Fade>

        {/* Gradient overlay for better text readability */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Content */}
      <Grid
        container
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          minHeight: "100vh",
          px: { xs: 2, md: 8 },
          py: 4,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* Left Side - Branding & Info */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            color: "white",
            pr: { md: 4 },
            minHeight: "100vh",
            transform: isLoaded ? "translateX(0)" : "translateX(-50px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.2s",
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                transform: isLoaded ? "translateY(0)" : "translateY(-20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.4s",
              }}
            >
              <img
                src={talentIq}
                alt="Logo"
                style={{
                  width: "60px",
                  height: "60px",
                  filter: "brightness(0) invert(1)",
                }}
                draggable={false}
              />
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: "bold",
                  color: "white",
                  ml: 2,
                }}
              >
                Talent IQ
              </Typography>
            </Box>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 400,
                transform: isLoaded ? "translateY(0)" : "translateY(10px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.6s",
              }}
            >
              Professional Assessment Platform
            </Typography>
          </Box>

          {/* Main Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: { xs: "40px", md: "52px" },
                fontWeight: "bold",
                lineHeight: 1.1,
                mb: 1,
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.3s",
              }}
            >
              Reset Your
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "40px", md: "52px" },
                fontWeight: "bold",
                color: "#62a7f8",
                lineHeight: 1.1,
                mb: 3,
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.4s",
              }}
            >
              Password
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontSize: "18px",
                lineHeight: 1.6,
                maxWidth: "500px",
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.5s",
              }}
            >
              Create a strong new password to secure your Talent IQ account.
              Make sure it's unique and memorable.
            </Typography>
          </Box>

          {/* Security Tips */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 3, md: 6 },
              flexWrap: "wrap",
              transform: isLoaded ? "translateY(0)" : "translateY(30px)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.8s ease-out",
              transitionDelay: "0.6s",
            }}
          >
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.7s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                🔐
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Strong Password
              </Typography>
            </Box>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.8s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                ⚡
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Quick Setup
              </Typography>
            </Box>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.9s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                🛡️
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Secure Access
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Reset Password Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            transform: isLoaded ? "translateX(0)" : "translateX(50px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.3s",
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              p: { xs: 3, md: 3 },
              width: "100%",
              maxWidth: "450px",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              transform: isLoaded
                ? "translateY(0) scale(1)"
                : "translateY(30px) scale(0.95)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.6s ease-out",
              transitionDelay: "0.5s",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#62a7f8",
                borderRadius: "2px",
              },
            }}
          >
            {/* Back Button */}
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{
                color: "#62a7f8",
                textTransform: "none",
                fontWeight: 500,
                mb: 1,
                pl: 0,
                minHeight: "40px",
                "&:hover": {
                  backgroundColor: "rgba(98, 167, 248, 0.1)",
                },
                transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.5s ease-out",
                transitionDelay: "0.6s",
              }}
            >
              Back to Forgot Password
            </Button>

            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Box
                  sx={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "rgba(98, 167, 248, 0.1)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                    transform: isLoaded ? "scale(1)" : "scale(0)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.7s",
                  }}
                >
                  <KeyOutlinedIcon sx={{ color: "#62a7f8", fontSize: 24 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "#2b4660",
                    mb: 1,
                    transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.8s",
                  }}
                >
                  Reset Password
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "#64748b",
                    transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.9s",
                  }}
                >
                  Enter the OTP sent to your email and create a new password
                </Typography>
              </Box>

              {/* Email Field */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "1s",
                  }}
                >
                  <Email sx={{ color: "#64748b", fontSize: 18 }} />
                  <Typography
                    sx={{ fontWeight: 500, color: "#2b4660", fontSize: "14px" }}
                  >
                    Email Address
                  </Typography>
                </Box>
                <TextField
                  value={email}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    sx: {
                      borderRadius: "10px",
                      backgroundColor: "rgba(245, 248, 251, 0.8)",
                      "& .MuiOutlinedInput-input": {
                        color: "#64748b",
                        fontSize: "14px",
                        padding: "10px 12px",
                      },
                      transform: isLoaded
                        ? "translateY(0)"
                        : "translateY(10px)",
                      opacity: isLoaded ? 1 : 0,
                      transition: "all 0.5s ease-out",
                      transitionDelay: "1.1s",
                    },
                  }}
                />
              </Box>

              {/* OTP Input Section */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "1.2s",
                  }}
                >
                  <KeyOutlinedIcon sx={{ color: "#64748b", fontSize: 18 }} />
                  <Typography
                    sx={{ fontWeight: 500, color: "#2b4660", fontSize: "14px" }}
                  >
                    Enter 6-digit Verification Code
                  </Typography>
                </Box>

                <OTPInput
                  value={otp}
                  onChange={handleOtpChange}
                  length={6}
                  disabled={otpExpired}
                />

                <Controller
                  name="otp"
                  control={control}
                  render={({ field }) => <input type="hidden" {...field} />}
                />

                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: otpExpired ? "#ef4444" : "#64748b",
                    mt: 1,
                    fontWeight: 500,
                    fontSize: "12px",
                    transform: isLoaded ? "translateY(0)" : "translateY(10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "1.3s",
                  }}
                >
                  {otpExpired
                    ? "OTP has expired - Please request a new one"
                    : `Time remaining: ${formatTime(timeLeft)}`}
                </Typography>
                {errors.otp && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#ef4444",
                      textAlign: "center",
                      mt: 1,
                      fontSize: "12px",
                    }}
                  >
                    {errors.otp.message}
                  </Typography>
                )}
              </Box>

              {/* Password Field */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "1.4s",
                  }}
                >
                  <Lock sx={{ color: "#64748b", fontSize: 18 }} />
                  <Typography
                    sx={{ fontWeight: 500, color: "#2b4660", fontSize: "14px" }}
                  >
                    New Password <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "white",
                          "&:hover fieldset": {
                            borderColor: "#62a7f8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#62a7f8",
                            borderWidth: "2px",
                          },
                          transform: isLoaded
                            ? "translateY(0)"
                            : "translateY(10px)",
                          opacity: isLoaded ? 1 : 0,
                          transition: "all 0.5s ease-out",
                          transitionDelay: "1.5s",
                        },
                        "& .MuiOutlinedInput-input": {
                          fontSize: "14px",
                          padding: "10px 12px",
                        },
                      }}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                              sx={{ color: "#64748b", padding: "4px" }}
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ fontSize: 18 }} />
                              ) : (
                                <Visibility sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Confirm Password Field */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "1.6s",
                  }}
                >
                  <Lock sx={{ color: "#64748b", fontSize: 18 }} />
                  <Typography
                    sx={{ fontWeight: 500, color: "#2b4660", fontSize: "14px" }}
                  >
                    Confirm Password <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="confirmPassword"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "white",
                          "&:hover fieldset": {
                            borderColor: "#62a7f8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#62a7f8",
                            borderWidth: "2px",
                          },
                          transform: isLoaded
                            ? "translateY(0)"
                            : "translateY(10px)",
                          opacity: isLoaded ? 1 : 0,
                          transition: "all 0.5s ease-out",
                          transitionDelay: "1.7s",
                        },
                        "& .MuiOutlinedInput-input": {
                          fontSize: "14px",
                          padding: "10px 12px",
                        },
                      }}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                    />
                  )}
                />
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                  opacity: isLoaded ? 1 : 0,
                  transition: "all 0.5s ease-out",
                  transitionDelay: "1.8s",
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => reset()}
                  fullWidth
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !isOtpEntered}
                  className="gradient-button"
                  fullWidth
                >
                  {loading ? "Updating..." : "Reset Password"}
                </Button>
              </Box>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPassword;
